import { useMemo, useCallback, useState, useEffect } from 'react';
import { atom, useAtom } from 'jotai';

const STORAGE_KEY_PREFIX = 'archipro:enquiry-submitted:';

/**
 * Jotai atom holding a Set of siteTreeIDs for which enquiries have been
 * submitted during this browser session. Provides real-time reactivity so
 * buttons update immediately when the EnquiryModal fires onSuccess.
 */
const enquirySubmittedSetAtom = atom<Set<number>>(new Set<number>());

/**
 * Tracks whether the user has submitted an enquiry for a given page.
 *
 * Uses a Jotai atom for instant cross-component reactivity (the tick appears
 * the moment the modal closes) and localStorage for persistence across
 * page navigations and browser sessions.
 *
 * @param siteTreeID The ID of the library item (product, professional,
 *   project, article). Pass `undefined` if not yet known.
 * @returns `hasSubmitted` — whether an enquiry has been submitted for this page.
 * @returns `markSubmitted` — call on successful enquiry submission.
 */
export function useEnquirySubmitted(siteTreeID: number | undefined) {
    const [submittedSet, setSubmittedSet] = useAtom(enquirySubmittedSetAtom);

    // Read localStorage only after mount. Doing it during the initial render
    // would diff against the SSR output and break hydration (React #418).
    const [persistedSubmitted, setPersistedSubmitted] = useState(false);
    useEffect(() => {
        if (siteTreeID === undefined) {
            setPersistedSubmitted(false);
            return;
        }
        try {
            // Set the exact value so it resets when navigating to a page that
            // has not been enquired (the hook instance is reused across nav).
            setPersistedSubmitted(
                localStorage.getItem(
                    STORAGE_KEY_PREFIX + String(siteTreeID),
                ) === '1',
            );
        } catch {
            setPersistedSubmitted(false);
        }
    }, [siteTreeID]);

    const hasSubmitted = useMemo(() => {
        if (siteTreeID === undefined) {
            return false;
        }

        // In-memory Jotai atom (real-time) or the persisted localStorage value.
        return submittedSet.has(siteTreeID) || persistedSubmitted;
    }, [siteTreeID, submittedSet, persistedSubmitted]);

    const markSubmitted = useCallback(() => {
        if (siteTreeID === undefined) {
            return;
        }

        // Update the Jotai atom for immediate reactivity.
        setSubmittedSet((prev: Set<number>) => {
            const next = new Set(prev);
            next.add(siteTreeID);
            return next;
        });

        // Persist to localStorage.
        if (typeof window !== 'undefined') {
            try {
                localStorage.setItem(
                    STORAGE_KEY_PREFIX + String(siteTreeID),
                    '1',
                );
            } catch {
                // localStorage may be full or unavailable — silently ignore.
            }
        }
    }, [siteTreeID, setSubmittedSet]);

    return { hasSubmitted, markSubmitted } as const;
}
